import { ApiVersionNumber } from "../api/services/data-service";
import { ABSRegions, AWSRegions, GoogleRegions } from "../helper/regions";
import { IAddShardRepositoryRequest, IShardRepositoryTestConnectionRequest, IUpdateShardRepositoryRequest, StorageType } from "../api/entities/shard-repository";

export const storageTypeToNameMap = {
  "S3": "S3",
  "GCS": "Google Cloud Storage",
  "ABS": "Azure Blob Storage"
};

const storageProviderTypeCategoryMap: Record<StorageType, StorageType> = {
  "S3": "S3",
  "s3p": "S3",
  "GCS": "GCS",
  "ABS": "ABS",
};

export const storageTypeToSecretFieldLabelMap: Record<StorageType, string> = {
  "S3": "Access Key Secret",
  "s3p": "Access Key Secret",
  "GCS": "Private Key",
  "ABS": "Account Key",
};

export const storageProviderTypeToRegionFieldLabelMap = {
  "S3": "Region",
  "GCS": "Region",
  "ABS": "Location",
};

export enum StorageProviderField {
  StorageName = "storageName",
  Region = "region",
  Bucket = "bucket",
  AccountName = "account_name",
  Container = "container",
  ProjectId = "project_id",
  AccessKey = "key",
  SecretKey = "secret",
  ClientEmail = "client_email",
  EndpointURL = "endpointURL",
  GCSConfigProps = "gcsConfigProps",
  SasUri = "sasUri",
}

type StorageProviderFieldInfo = {
  providers?: StorageType[];
  optional?: boolean;
  sensitive?: boolean;
  v1?: VersionFieldEnabledInfo;
  v2?: VersionFieldEnabledInfo;
};

type VersionFieldEnabledInfo = {
  providers?: StorageType[];
  enabled: boolean;
}

type StorageNodeRequest = IAddShardRepositoryRequest | IUpdateShardRepositoryRequest | IShardRepositoryTestConnectionRequest;

const storageProviderFieldsInfo: {[key in StorageProviderField]: StorageProviderFieldInfo} = {
  [StorageProviderField.StorageName]: { },
  [StorageProviderField.Region]: {
    providers: ["S3", "s3p", "ABS", "GCS"],
    optional: true
  },
  [StorageProviderField.Bucket]: {
    providers: ["S3", "s3p", "GCS"]
  },
  [StorageProviderField.AccountName]: {
    providers: ["ABS"],
    sensitive: true,
    v2: {
      enabled: false
    }
  },
  [StorageProviderField.Container]: {
    providers: ["ABS"]
  },
  [StorageProviderField.SasUri]: {
    providers: ["ABS"],
    sensitive: true,
    v1: {
      enabled: false
    }
  },
  [StorageProviderField.ProjectId]: {
    providers: ["GCS"],
    sensitive: true,
    v2: {
      enabled: false
    }
  },
  [StorageProviderField.AccessKey]: {
    providers: ["S3", "s3p"],
    sensitive: true
  },
  [StorageProviderField.SecretKey]: {
    sensitive: true,
    v2: {
      providers: ["GCS", "ABS"],
      enabled: false
    }
  },
  [StorageProviderField.ClientEmail]: {
    providers: ["GCS"],
    sensitive: true,
    v2: {
      enabled: false
    }
  },
  [StorageProviderField.EndpointURL]: {
    providers: ["S3", "s3p"],
    optional: true
  },
  [StorageProviderField.GCSConfigProps]: {
    providers: ["GCS"],
    sensitive: true
  }
};

export class StorageUtils {

  getStorageProviderTypeGeneralizedType(storageProvider: StorageType): StorageType {
    return storageProviderTypeCategoryMap[storageProvider];
  }

  getProviderTypeRegions(provider: StorageType): string[] {
    switch (provider) {
      case "S3":
        return [...AWSRegions];
      case "ABS":
        return [...ABSRegions];
      case "GCS":
        return [...GoogleRegions];
      default: 
        return [];
    }
  }

  isCustomRegion(provider: StorageType, region: string): boolean {
    return this.getProviderTypeRegions(provider).indexOf(region) < 0;
  }

  isFieldVisible(field: StorageProviderField, provider: StorageType, showSensitiveFields: boolean): boolean {
    const info = storageProviderFieldsInfo[field];
    return (info && (!info.providers || info.providers.includes(provider)) && ((!showSensitiveFields && !info.sensitive) || showSensitiveFields));
  }

  isFieldOptional(field: StorageProviderField): boolean {
    const info = storageProviderFieldsInfo[field];
    return (info && !!info.optional);
  }

  isFieldEnabled(field: StorageProviderField, provider: StorageType, version: ApiVersionNumber): boolean {
    const info = storageProviderFieldsInfo[field];

    const includesProvider = (p: StorageType, version: string) =>
        (!!p && info[version].providers &&
            ((info[version].providers.includes(p) && info[version].enabled) ||
                (!info[version].providers.includes(p) && !info[version].enabled))) ||
        (!info[version].providers && info[version].enabled);

    const versionPropName = `v${version}`;
    return (!!info && (!info[versionPropName] || includesProvider(provider, versionPropName)));
  }

  cleanNotApplicableFields<TRequest extends StorageNodeRequest>(request: TRequest, showSensitiveFields: boolean): TRequest {
    Object.keys(storageProviderFieldsInfo).forEach((field: StorageProviderField) => {
      if (request[field] && !this.isFieldVisible(field, request.storageType, showSensitiveFields)) {
        request[field] = null;
      }
    });
    return request;
  }

  isTestConnectionRequestValid(request: IShardRepositoryTestConnectionRequest, showSensitiveFields: boolean): boolean {
    return Object.keys(storageProviderFieldsInfo)
        .filter((field: StorageProviderField) => this.isFieldEnabled(field, request.storageType, 2))
        .every((field: StorageProviderField) => {
          return field === StorageProviderField.StorageName || request[field] ||
              !this.isFieldVisible(field, request.storageType, showSensitiveFields) ||
              this.isFieldOptional(field);
        });
  }
}

export const storageUtils = new StorageUtils();
